import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Menu, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import Logo from "../../assets/Logo.svg?react";

const Navbar = ({ activeSection, toggleLanguage, lang }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  // Detectar scroll para cambiar estilo del navbar
  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleClick = (item) => {
    const goToSection = () => {
      const section = document.getElementById(item);
      if (section) section.scrollIntoView({ behavior: "smooth" });
    };

    const isInHome = location.pathname === `/${lang}` || location.pathname === "/";

    if (item === "hero") {
      if (isInHome) {
        window.scrollTo({ top: 0, behavior: "smooth" });
      } else {
        navigate(`/${lang}`, { state: { skipAnimation: true } });
      }
      return;
    }

    if (isInHome) {
      goToSection();
    } else {
      navigate(`/${lang}`, { state: { skipAnimation: true } });
      setTimeout(() => goToSection(), 300);
    }
  };

  const isServiciosPage = location.pathname.includes("/servicios");
  const isNosotrosPage = location.pathname.includes("/nosotros");
  const isContactoPage = location.pathname.includes("/contacto");
  const isBlogPage = location.pathname.includes("/blog");
  const isHomePage = location.pathname === `/${lang}` || location.pathname === "/";

  return (
    <>
      {/* Navbar principal */}
      <motion.nav
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled
            ? "bg-black/90 backdrop-blur-xl border-b border-[#1de9b6]/20 shadow-lg shadow-black/50"
            : "bg-transparent"
        }`}
      >
        <div className="flex items-center justify-between px-6 lg:px-12 py-4 max-w-7xl mx-auto">
          {/* Logo ZENNITH */}
          <motion.div
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="flex items-center gap-3 cursor-pointer group"
            onClick={() => {
              if (isHomePage) {
                window.scrollTo({ top: 0, behavior: "smooth" });
              } else {
                navigate(`/${lang}`, { state: { skipAnimation: true } });
              }
            }}
          >
            <div className="relative">
              <Logo className="h-10 w-10 transition-all duration-300 group-hover:rotate-12" />
              <div className="absolute inset-0 bg-[#1de9b6]/20 rounded-full blur-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
            </div>
            <h1 className="text-2xl lg:text-3xl font-black text-transparent bg-gradient-to-r from-[#1de9b6] to-[#0fb] bg-clip-text tracking-wide group-hover:brightness-125 transition-all duration-300">
              ZENNITH
            </h1>
          </motion.div>

          {/* Menú Desktop */}
          <div className="hidden lg:flex items-center gap-1">
            {["hero", "servicios", "nosotros", "contacto", "blog"].map((item, idx) => {
              const isActive =
                item === "hero"
                  ? isHomePage || activeSection === "hero"
                  : item === "servicios"
                  ? isServiciosPage || activeSection === "servicios"
                  : item === "nosotros"
                  ? isNosotrosPage
                  : item === "contacto"
                  ? isContactoPage
                  : isBlogPage;

              const isLink = ["servicios", "nosotros", "contacto", "blog"].includes(item);

              return (
                <motion.div key={idx} whileHover={{ y: -2 }} className="relative">
                  {isLink ? (
                    <Link
                      to={`/${lang}/${item}`}
                      className={`relative px-4 py-2 text-sm font-medium transition-all duration-300 rounded-lg ${
                        isActive
                          ? "text-[#1de9b6] bg-[#1de9b6]/10"
                          : "text-gray-300 hover:text-white hover:bg-white/5"
                      }`}
                    >
                      {item.charAt(0).toUpperCase() + item.slice(1)}
                      {isActive && (
                        <motion.div
                          layoutId="activeTab"
                          className="absolute inset-0 bg-[#1de9b6]/20 rounded-lg border border-[#1de9b6]/30"
                          transition={{ duration: 0.3 }}
                        />
                      )}
                    </Link>
                  ) : (
                    <button
                      onClick={() => handleClick(item)}
                      className={`relative px-4 py-2 text-sm font-medium transition-all duration-300 rounded-lg cursor-pointer ${
                        isActive
                          ? "text-[#1de9b6] bg-[#1de9b6]/10"
                          : "text-gray-300 hover:text-white hover:bg-white/5"
                      }`}
                    >
                      {t(`nav.${item}`)}
                      {isActive && (
                        <motion.div
                          layoutId="activeTab"
                          className="absolute inset-0 bg-[#1de9b6]/20 rounded-lg border border-[#1de9b6]/30"
                          transition={{ duration: 0.3 }}
                        />
                      )}
                    </button>
                  )}
                </motion.div>
              );
            })}
          </div>

          {/* Controles derecha */}
          <div className="flex items-center gap-3">
            {/* 🌐 Botón idioma desktop */}
            <motion.button
              whileHover={{ scale: 1.08, rotate: 2 }}
              whileTap={{ scale: 0.92 }}
              onClick={toggleLanguage}
              className={`hidden lg:flex relative items-center gap-2 px-5 py-2 text-sm font-semibold rounded-full cursor-pointer
                transition-all duration-500 overflow-hidden backdrop-blur-md
                border border-[#1de9b6]/40 shadow-inner shadow-[#1de9b6]/10
                ${
                  scrolled
                    ? "text-[#00fff2] bg-gradient-to-r from-[#00fff2]/10 via-[#00ffcc]/5 to-[#00fff2]/10 hover:bg-[#00fff2]/15 hover:border-[#00fff2]/60"
                    : "text-[#1de9b6] bg-gradient-to-r from-[#1de9b6]/10 via-[#00ffcc]/5 to-[#1de9b6]/10 hover:bg-[#1de9b6]/15 hover:border-[#1de9b6]/60"
                }`}
            >
              <motion.div
                className={`absolute inset-0 bg-gradient-to-r ${
                  scrolled
                    ? "from-transparent via-[#00fff2]/25 to-transparent"
                    : "from-transparent via-[#1de9b6]/25 to-transparent"
                }`}
                animate={{ x: ["-100%", "100%"] }}
                transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
              />
              <motion.div
                className="absolute inset-0 rounded-full blur-xl opacity-0 hover:opacity-50 transition-opacity duration-500"
                style={{
                  background: scrolled
                    ? "radial-gradient(circle at center, #00fff2 0%, transparent 70%)"
                    : "radial-gradient(circle at center, #1de9b6 0%, transparent 70%)",
                }}
              />
              <span className="relative z-10">{lang === "es" ? "ES" : "EN"}</span>
            </motion.button>

            {/* 🌐 Botón idioma móvil */}
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
              onClick={toggleLanguage}
              className={`relative px-3 py-1 text-sm font-semibold rounded-full cursor-pointer overflow-hidden
                border backdrop-blur-md transition-all duration-500
                ${
                  scrolled
                    ? "text-[#00fff2] border-[#00fff2]/40 bg-black/50 hover:bg-[#00fff2]/10"
                    : "text-[#1de9b6] border-[#1de9b6]/40 bg-black/50 hover:bg-[#1de9b6]/10"
                } lg:hidden`}
            >
              <motion.div
                className={`absolute inset-0 bg-gradient-to-r ${
                  scrolled
                    ? "from-transparent via-[#00fff2]/25 to-transparent"
                    : "from-transparent via-[#1de9b6]/25 to-transparent"
                }`}
                animate={{ x: ["-100%", "100%"] }}
                transition={{ duration: 2.5, repeat: Infinity, ease: "linear" }}
              />
              <span className="relative z-10">{lang === "es" ? "ES" : "EN"}</span>
            </motion.button>

            {/* Menú móvil */}
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setMenuOpen(!menuOpen)}
              className="p-2 text-[#1de9b6] hover:bg-[#1de9b6]/10 rounded-lg transition-colors lg:hidden"
              aria-label="Abrir menú"
            >
              <AnimatePresence mode="wait">
                {menuOpen ? (
                  <motion.div
                    key="close"
                    initial={{ rotate: -90, opacity: 0 }}
                    animate={{ rotate: 0, opacity: 1 }}
                    exit={{ rotate: 90, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    <X size={24} />
                  </motion.div>
                ) : (
                  <motion.div
                    key="menu"
                    initial={{ rotate: 90, opacity: 0 }}
                    animate={{ rotate: 0, opacity: 1 }}
                    exit={{ rotate: -90, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    <Menu size={24} />
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.button>
          </div>
        </div>
      </motion.nav>

      {/* Menú móvil */}
      <AnimatePresence>
        {menuOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-[60] bg-black/80 backdrop-blur-sm lg:hidden"
              onClick={() => setMenuOpen(false)}
            />
            <motion.div
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "tween", duration: 0.3, ease: "easeOut" }}
              className="fixed top-0 right-0 bottom-0 z-[61] w-80 max-w-[90vw] bg-gradient-to-br from-black via-gray-950 to-black border-l border-[#1de9b6]/20 lg:hidden"
            >
              <div className="flex flex-col h-full">
                <div className="flex items-center justify-between p-6 border-b border-[#1de9b6]/10">
                  <div className="flex items-center gap-3">
                    <Logo className="h-8 w-8" />
                    <span className="text-xl font-black text-transparent bg-gradient-to-r from-[#1de9b6] to-[#0fb] bg-clip-text">
                      ZENNITH
                    </span>
                  </div>
                  <motion.button
                    whileHover={{ rotate: 90 }}
                    whileTap={{ scale: 0.9 }}
                    onClick={() => setMenuOpen(false)}
                    className="p-2 text-[#1de9b6] hover:bg-[#1de9b6]/10 rounded-lg transition-colors"
                    aria-label="Cerrar menú"
                  >
                    <X size={20} />
                  </motion.button>
                </div>

                <nav className="flex-1 p-6 space-y-2">
                  {["hero", "servicios", "nosotros", "contacto", "blog"].map((item, idx) => {
                    const isActive =
                      item === "hero"
                        ? isHomePage || activeSection === "hero"
                        : item === "servicios"
                        ? isServiciosPage || activeSection === "servicios"
                        : item === "nosotros"
                        ? isNosotrosPage
                        : item === "contacto"
                        ? isContactoPage
                        : isBlogPage;
                    const isLink = ["servicios", "nosotros", "contacto", "blog"].includes(item);

                    return (
                      <motion.div
                        key={idx}
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: idx * 0.1, duration: 0.3 }}
                        whileHover={{ x: 5 }}
                        className="relative"
                      >
                        {isLink ? (
                          <Link
                            to={`/${lang}/${item}`}
                            onClick={() => setMenuOpen(false)}
                            className={`flex items-center gap-3 px-4 py-3 text-base font-medium rounded-xl transition-all duration-300 ${
                              isActive
                                ? "text-[#1de9b6] bg-[#1de9b6]/10 border border-[#1de9b6]/20"
                                : "text-gray-300 hover:text-white hover:bg-white/5"
                            }`}
                          >
                            <div
                              className={`w-2 h-2 rounded-full transition-all ${
                                isActive ? "bg-[#1de9b6]" : "bg-gray-600"
                              }`}
                            />
                            {item.charAt(0).toUpperCase() + item.slice(1)}
                          </Link>
                        ) : (
                          <button
                            onClick={() => {
                              handleClick(item);
                              setTimeout(() => setMenuOpen(false), 300);
                            }}
                            className={`w-full flex items-center gap-3 px-4 py-3 text-base font-medium rounded-xl transition-all duration-300 ${
                              isActive
                                ? "text-[#1de9b6] bg-[#1de9b6]/10 border border-[#1de9b6]/20"
                                : "text-gray-300 hover:text-white hover:bg-white/5"
                            }`}
                          >
                            <div
                              className={`w-2 h-2 rounded-full transition-all ${
                                isActive ? "bg-[#1de9b6]" : "bg-gray-600"
                              }`}
                            />
                            {t(`nav.${item}`)}
                          </button>
                        )}
                      </motion.div>
                    );
                  })}
                </nav>

                <div className="p-6 border-t border-[#1de9b6]/10 text-center text-sm text-gray-500">
                  Navegación Móvil
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
